/** Thin client for the studio API. Every state-changing call carries X-Studio (CSRF guard). */

import type { Asset, Project } from "@studio/engine";
import { appUrl } from "./base";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

async function call<T>(method: string, url: string, body?: unknown, raw?: BodyInit): Promise<T> {
  const headers: Record<string, string> = { "X-Studio": "1" };
  let payload: BodyInit | undefined = raw;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const r = await fetch(appUrl(url), { method, headers, body: payload, credentials: "same-origin" });
  if (r.status === 401 && !url.endsWith("/login")) {
    window.dispatchEvent(new Event("studio:logged-out"));
  }
  const text = await r.text();
  const data = text ? JSON.parse(text) : null;
  if (!r.ok) {
    const d = data?.detail;
    const msg = typeof d === "string" ? d : d?.message ?? (Array.isArray(d) ? d.map((x) => x.msg).join("; ") : r.statusText);
    throw new ApiError(r.status, msg, d);
  }
  return data as T;
}

export interface User {
  id: string;
  email: string;
  name: string;
  admin: boolean;
  disabled: boolean;
  created: number;
  last_login: number | null;
}

export interface Invite {
  id: string;
  email: string;
  admin: boolean;
  created: number;
  expires: number;
}

export interface Member {
  id: string;
  email: string;
  name: string;
  role: "owner" | "editor";
}

/** An emailed link; `link` comes back too so an admin can pass it on by hand. */
export interface SentLink {
  link: string;
  sent: boolean;
}

export interface HealthState {
  checked: number | null;
  interval: number;
  checks: Record<string, { ok: boolean; since: number; detail: string; fails: number; alerted: number | null }>;
  recipients: string[];
  email: boolean;
  ping: boolean;
}

export interface ProjectSummary {
  id: string;
  name: string;
  updated: number;
  scenes: number;
  elements: number;
  thumb: string | null;
  role: "owner" | "editor";
}

export interface Job {
  id: string;
  project: string;
  kind: "analyze" | "render" | "still" | "import" | "subtitles" | "voice" | "tts" | "package";
  status: "queued" | "running" | "done" | "error" | "cancelled" | "cancelling";
  progress: number;
  eta: number | null;
  message: string;
  params: Record<string, unknown> & { assetId?: string };
  output: { asset?: Asset; sentences?: number; segments?: VoiceSegment[]; file?: string; size?: number; frames?: number; seconds?: number; fps?: number; duration?: number; srt?: string; vtt?: string; cues?: number; deleted?: boolean } | null;
  created: number;
  started: number | null;
  finished: number | null;
  log?: string;
}

export interface AnalysisState {
  job: Job | null;
  ready: boolean;
  stamp?: number;
  stats?: Record<string, number>;
  params?: Record<string, number | string | null>;
  /** analysed (cropped) layer size in px */
  size?: [number, number];
  /** crop of the layer inside the source image [x, y, w, h], and the source size */
  crop?: [number, number, number, number];
  source?: [number, number];
  /** build step: what it was compared against and how much changed */
  diff?: { against: string; changed: number } | null;
}

export interface PackageCheck {
  ok: boolean;
  problems: string[];
  warnings: string[];
  summary: { name: string; scenes: number; layers: number; audio: number; cues: number; files: number } | null;
}

export interface VoiceSegment {
  start: number;
  end: number;
  text: string;
}

export interface ReviewComment {
  id: string;
  t: number;
  name: string;
  text: string;
  created: number;
  resolved: boolean;
  /** the review link it came through, and the render the reviewer was watching */
  link: string;
  render: string | null;
}

/** What a review link shows: the newest render, every render, the chosen files, or a live preview drawn in the browser (no render). */
export type LinkRenders = "latest" | "all" | "live" | string[];

export interface ReviewLink {
  id: string;
  name: string;
  path: string;
  renders: LinkRenders;
  expires: number | null;
  comments: boolean;
  paused: boolean;
  revoked: number | null;
  created: number;
  lastOpened: number | null;
  views: number;
  status: "active" | "paused" | "expired" | "revoked";
  commentCount: number;
  /** live preview: follow the edits, or play the copy frozen at `snapshot` (project revision `snapshotRevision`) */
  follow: boolean | null;
  snapshot: number | null;
  snapshotRevision: number | null;
}

export interface RenderFile {
  file: string;
  created: number;
  start: number;
  end: number | null;
  duration: number | null;
  quality: string | null;
  fps: number | null;
  scope: string;
  revision: number | null;
  size: number;
  srt: string | null;
  vtt: string | null;
  label: string;
  note: string;
  final: boolean;
  job: string | null;
  /** names of the active review links that show it */
  links: string[];
}

export interface UploadResult {
  name?: string;
  error?: string;
  asset?: Asset;
  job?: Job | null;
}

export interface LibraryItem {
  id: string;
  kind: "image" | "audio" | "font";
  name: string;
  file: string;
  size: number;
  width: number | null;
  height: number | null;
  duration: number | null;
  by: string | null;
  byName: string;
  created: number;
  archived: boolean;
  tags: string[];
  thumb: boolean;
}

export interface LibraryQuery {
  q?: string;
  kind?: LibraryItem["kind"] | "";
  tags?: string[];
  archived?: boolean;
}

/** One file of a library upload: the stored item (`duplicate` if it was already there) or an error. */
export interface LibraryResult {
  name?: string;
  error?: string;
  item?: LibraryItem;
  duplicate?: boolean;
}

export const api = {
  session: () => call<{ loggedIn: boolean; user: User | null }>("GET", "/api/session"),
  login: (email: string, password: string) => call<{ user: User }>("POST", "/api/login", { email, password }),
  logout: () => call("POST", "/api/logout"),
  forgot: (email: string) => call("POST", "/api/password/forgot", { email }),
  resetPassword: (token: string, password: string) => call("POST", "/api/password/reset", { token, password }),
  inviteCheck: (token: string) => call<{ email: string }>("POST", "/api/invite/check", { token }),
  inviteAccept: (token: string, name: string, password: string) => call<{ user: User }>("POST", "/api/invite/accept", { token, name, password }),
  updateAccount: (name: string) => call<User>("PATCH", "/api/account", { name }),
  changePassword: (current: string, password: string) => call("POST", "/api/account/password", { current, password }),
  health: () => call<HealthState>("GET", "/api/admin/health"),
  healthTest: () => call<{ sent: boolean }>("POST", "/api/admin/health/test"),
  oauthRequest: (rid: string) => call<{ client: string; redirect: string; access: string }>("GET", `/api/oauth/requests/${rid}`),
  oauthAnswer: (rid: string, allow: boolean, edit = false) => call<{ redirect: string }>("POST", `/api/oauth/requests/${rid}`, { allow, edit }),
  setAppAccess: (id: string, access: "read" | "edit") => call("PATCH", `/api/oauth/grants/${id}`, { access }),
  connectedApps: () => call<{ id: string; client_name: string; access: string; created: number; used: number | null }[]>("GET", "/api/oauth/grants"),
  disconnectApp: (id: string) => call("DELETE", `/api/oauth/grants/${id}`),
  vocabulary: () => call<{ text: string }>("GET", "/api/admin/vocabulary"),
  saveVocabulary: (text: string) => call<{ terms: number }>("PUT", "/api/admin/vocabulary", { text }),
  adminUsers: () => call<{ users: User[]; invites: Invite[]; email: boolean }>("GET", "/api/admin/users"),
  invite: (email: string, admin: boolean) => call<SentLink>("POST", "/api/admin/invites", { email, admin }),
  revokeInvite: (id: string) => call("DELETE", `/api/admin/invites/${id}`),
  updateUser: (id: string, patch: { admin?: boolean; disabled?: boolean }) => call<User>("PATCH", `/api/admin/users/${id}`, patch),
  sendReset: (id: string) => call<SentLink>("POST", `/api/admin/users/${id}/reset`),
  members: (pid: string) => call<Member[]>("GET", `/api/projects/${pid}/members`),
  addMember: (pid: string, email: string) => call<Member[]>("POST", `/api/projects/${pid}/members`, { email }),
  removeMember: (pid: string, uid: string) => call("DELETE", `/api/projects/${pid}/members/${uid}`),
  projects: () => call<ProjectSummary[]>("GET", "/api/projects"),
  create: (name: string) => call<Project>("POST", "/api/projects", { name }),
  project: (id: string) => call<Project>("GET", `/api/projects/${id}`),
  save: (p: Project) => call<{ revision: number }>("PUT", `/api/projects/${p.id}`, p),
  history: (id: string) => call<{ file: string; time: number; revision: number; scenes: number; elements: number }[]>("GET", `/api/projects/${id}/history`),
  historyVersion: (id: string, file: string) => call<Project>("GET", `/api/projects/${id}/history/${file}`),
  duplicate: (id: string) => call<Project>("POST", `/api/projects/${id}/duplicate`),
  remove: (id: string) => call("DELETE", `/api/projects/${id}`),
  upload: (id: string, files: File[], kind?: "hand") => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    if (kind) fd.append("kind", kind);
    return call<UploadResult[]>("POST", `/api/projects/${id}/assets`, undefined, fd);
  },
  analyze: (id: string, assetId: string, params: object) => call<Job>("POST", `/api/projects/${id}/assets/${assetId}/analyze`, { params }),
  analysis: (id: string) => call<Record<string, AnalysisState>>("GET", `/api/projects/${id}/analysis`),
  render: (id: string, body: object) => call<Job>("POST", `/api/projects/${id}/render`, body),
  still: (id: string, t: number, quality = "full") => call<Job>("POST", `/api/projects/${id}/still`, { t, quality }),
  generateSubtitles: (id: string) => call<Job>("POST", `/api/projects/${id}/subtitles/generate`),
  importShotlist: (id: string, body: { text: string; filename: string; align: boolean; gap: number }) => call<Job>("POST", `/api/projects/${id}/import`, body),
  reviewLinks: (id: string) => call<{ links: ReviewLink[] }>("GET", `/api/projects/${id}/review`),
  createLink: (id: string, body: { name: string; renders: LinkRenders; expires: number | null; comments: boolean; follow: boolean }) =>
    call<ReviewLink>("POST", `/api/projects/${id}/review`, body),
  updateLink: (id: string, lid: string, body: Partial<{ name: string; renders: LinkRenders; expires: number | null; comments: boolean; paused: boolean; follow: boolean; revoke: boolean; refreeze: boolean }>) =>
    call<ReviewLink>("PATCH", `/api/projects/${id}/review/${lid}`, body),
  deleteLink: (id: string, lid: string) => call("DELETE", `/api/projects/${id}/review/${lid}`),
  renders: (id: string) => call<{ renders: RenderFile[]; totalSize: number }>("GET", `/api/projects/${id}/renders`),
  updateRender: (id: string, file: string, body: Partial<{ label: string; note: string; final: boolean }>) =>
    call("PATCH", `/api/projects/${id}/renders/${encodeURIComponent(file)}`, body),
  deleteRender: (id: string, file: string) => call<{ freed: number }>("DELETE", `/api/projects/${id}/renders/${encodeURIComponent(file)}`),
  comments: (id: string) => call<ReviewComment[]>("GET", `/api/projects/${id}/comments`),
  resolveComment: (id: string, cid: string, resolved: boolean) => call("PATCH", `/api/projects/${id}/comments/${cid}`, { resolved }),
  deleteComment: (id: string, cid: string) => call("DELETE", `/api/projects/${id}/comments/${cid}`),
  library: ({ q = "", kind = "", tags = [], archived = false }: LibraryQuery) =>
    call<{ items: LibraryItem[]; tags: { tag: string; n: number }[] }>(
      "GET",
      `/api/library?${new URLSearchParams({ q, kind, tags: tags.join(","), archived: String(archived) })}`,
    ),
  libraryUpload: (files: File[], tags: string[]) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    fd.append("tags", tags.join(","));
    return call<LibraryResult[]>("POST", "/api/library", undefined, fd);
  },
  libraryUpdate: (id: string, patch: { name?: string; tags?: string[]; archived?: boolean }) => call<LibraryItem>("PATCH", `/api/library/${id}`, patch),
  libraryTag: (ids: string[], add: string[], remove: string[]) => call("POST", "/api/library/tags", { ids, add, remove }),
  libraryDelete: (id: string) => call("DELETE", `/api/library/${id}`),
  fromLibrary: (pid: string, ids: string[]) => call<UploadResult[]>("POST", `/api/projects/${pid}/library`, { ids }),
  toLibrary: (pid: string, assetIds: string[], tags: string[] = []) => call<LibraryResult[]>("POST", `/api/projects/${pid}/assets/to-library`, { assetIds, tags }),
  voices: () => call<{ id: string; label: string }[]>("GET", "/api/voices"),
  voice: (pid: string, assetId: string, body: { mode: "script" | "preview" | "speak"; voice?: string; speed?: number; keepTiming?: boolean; segments?: VoiceSegment[] }) =>
    call<Job>("POST", `/api/projects/${pid}/assets/${assetId}/voice`, body),
  checkPackage: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return call<PackageCheck>("POST", "/api/packages?check=true", undefined, fd);
  },
  purgeAssets: (id: string) => call<{ removed: number; bytes: number }>("POST", `/api/projects/${id}/assets/purge`),
  importPackage: (file: File, opts: { into?: string; library?: boolean } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    const q = new URLSearchParams();
    if (opts.into) q.set("into", opts.into);
    if (opts.library) q.set("library", "true");
    return call<Job>("POST", `/api/packages${q.size ? `?${q}` : ""}`, undefined, fd);
  },
  voiceovers: () => call<Job[]>("GET", "/api/voiceovers"),
  createVoiceover: (body: { title: string; text: string; voice: string; speed: number; pause: number; preview: boolean }) => call<Job>("POST", "/api/voiceovers", body),
  voiceoverToLibrary: (id: string, name: string, tags: string[]) => call<LibraryResult>("POST", `/api/voiceovers/${id}/library`, { name, tags }),
  deleteVoiceover: (id: string) => call("DELETE", `/api/voiceovers/${id}`),
  jobs: (project: string, kind?: string) => call<Job[]>("GET", `/api/jobs?project=${project}${kind ? `&kind=${kind}` : ""}`),
  job: (id: string) => call<Job>("GET", `/api/jobs/${id}`),
  cancel: (id: string) => call<Job>("POST", `/api/jobs/${id}/cancel`),
};

export const filesBase = (pid: string) => appUrl(`/api/projects/${pid}/files`);
export const fileUrl = (pid: string, rel: string, v?: number | string) =>
  `${filesBase(pid)}/${rel}${v !== undefined ? `?v=${v}` : ""}`;
export const libraryUrl = (id: string, what: "file" | "thumb" = "file", download = false) =>
  appUrl(`/api/library/${id}/${what}${download ? "?download=1" : ""}`);
